// CordOption — compact cord selector inside the product purchase panel.
// "Med ledning" is preselected. When chosen, an expanded sub-menu appears
// where the customer picks cord length (3/5/7 m) and optionally adds
// afbryder and/or stikprop as separate line items.

import { useEffect, useMemo, useState } from "react";
import { fetchProduct, formatMoney, type ProductDetail } from "@/lib/shopify";
import { useLocale } from "@/lib/i18n";
import { Check } from "lucide-react";

export function isCordEligible(productType: string | undefined | null, title: string): boolean {
  const hay = `${productType ?? ""} ${title}`.toLowerCase();
  if (/bordlamp/.test(hay)) return false;
  return /(væglamp|vaeglamp|loftlamp|loftslamp|wall lamp|ceiling lamp|pendant)/.test(hay);
}

export type CordKind = "ceiling" | "wall";

export function cordKind(productType: string | undefined | null, title: string): CordKind {
  const hay = `${productType ?? ""} ${title}`.toLowerCase();
  if (/(væglamp|vaeglamp|wall lamp)/.test(hay)) return "wall";
  return "ceiling";
}

const CORD_HANDLE = "ledning-3m-afbryder";

export type CordVariant = {
  id: string;
  title: string;
  price: { amount: string; currencyCode: string };
  availableForSale: boolean;
  quantityAvailable?: number | null;
  selectedOptions: Array<{ name: string; value: string }>;
};

const isCord = (t: string) => /ledning/i.test(t);
const isSwitch = (t: string) => /(afbryder|switch)/i.test(t);
const isPlug = (t: string) => /(stik|plug)/i.test(t);

/**
 * Hook: fetches the cord product, derives the selectable variants and
 * exposes the user's selection. Returns the variants that should be
 * added as line items alongside the lamp.
 */
export function useCordAddon(kind: CordKind = "wall") {
  const [product, setProduct] = useState<ProductDetail | null>(null);
  // Default: both wall and ceiling lamps default to "no extra cord".
  // Wall lamps come without a cord; ceiling lamps come with a short original stub.
  const [withCord, setWithCord] = useState(false);
  const [cordId, setCordId] = useState<string | null>(null);
  const [extras, setExtras] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    fetchProduct(CORD_HANDLE).then((p) => { if (alive && p) setProduct(p); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const variants: CordVariant[] = useMemo(
    () => (product?.variants.edges ?? []).map((e) => e.node as CordVariant),
    [product],
  );
  const cordVariants = useMemo(
    () => variants.filter((v) => isCord(v.title) && v.availableForSale),
    [variants],
  );
  const extraVariants = useMemo(
    () => variants.filter((v) => !isCord(v.title) && (isSwitch(v.title) || isPlug(v.title)) && v.availableForSale),
    [variants],
  );

  // Preselect first cord (typically the shortest) when product loads.
  useEffect(() => {
    if (!cordId && cordVariants[0]) setCordId(cordVariants[0].id);
  }, [cordVariants, cordId]);

  const toggleExtra = (id: string) => {
    setExtras((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // For ceiling lamps we never offer plug/switch extras — only cord length.
  const effectiveExtras = kind === "ceiling" ? new Set<string>() : extras;
  const selected: CordVariant[] = withCord
    ? [
        ...(cordId ? variants.filter((v) => v.id === cordId) : []),
        ...variants.filter((v) => effectiveExtras.has(v.id)),
      ]
    : [];

  return {
    product,
    cordVariants,
    extraVariants,
    cordId,
    setCordId,
    extras,
    toggleExtra,
    withCord,
    setWithCord,
    selected,
    hasCordOption: cordVariants.length > 0,
    kind,
  };
}

type Props = ReturnType<typeof useCordAddon>;

export function CordSelector(props: Props) {
  const locale = useLocale();
  const {
    cordVariants, extraVariants, cordId, setCordId,
    extras, toggleExtra, withCord, setWithCord, hasCordOption, kind,
  } = props;
  const showExtras = kind !== "ceiling";

  if (!hasCordOption) return null;

  const copy = locale === "en"
    ? kind === "ceiling"
      ? {
          title: "Choose cord",
          helper: "The lamp comes with a short original cord. Keep it or add a new one.",
          none: "Original cord (included)",
          with: "New cord",
          reassure: "We fit it for you — ready to plug in",
          cordLabel: "Cord length",
          extrasLabel: "Add",
        }
      : {
          title: "Choose cord",
          helper: "The lamp comes without a cord. Add a new one if you'd like it ready to plug in.",
          none: "No cord",
          with: "New cord",
          reassure: "We fit it for you — ready to plug in",
          cordLabel: "Cord length",
          extrasLabel: "Add",
        }
    : kind === "ceiling"
      ? {
          title: "Vælg ledning",
          helper: "Lampen kommer med en stump original ledning. Behold den, eller tilføj en ny.",
          none: "Original ledning (medfølger)",
          with: "Ny ledning",
          reassure: "Vi monterer for dig – klar til brug",
          cordLabel: "Længde",
          extrasLabel: "Tilføj",
        }
      : {
          title: "Vælg ledning",
          helper: "Lampen kommer uden ledning. Tilføj en ny, hvis den skal være klar til brug.",
          none: "Ingen ledning",
          with: "Ny ledning",
          reassure: "Vi monterer for dig – klar til brug",
          cordLabel: "Længde",
          extrasLabel: "Tilføj",
        };

  return (
    <div className="mt-8 border-t border-border pt-6">
      <p className="text-[10px] uppercase tracking-[0.24em] text-foreground">{copy.title}</p>
      <p className="mt-1.5 text-xs text-muted-foreground">{copy.helper}</p>

      <div className="mt-3 grid grid-cols-2 gap-2" role="radiogroup" aria-label={copy.title}>
        <button
          type="button"
          role="radio"
          aria-checked={!withCord}
          onClick={() => setWithCord(false)}
          className={`flex items-center gap-2.5 rounded-sm border px-3 py-3 text-left text-sm transition-colors ${
            !withCord ? "border-foreground bg-background" : "border-border bg-background/60 hover:border-foreground/60"
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 flex-shrink-0 rounded-full border ${
              !withCord ? "border-foreground bg-foreground" : "border-border"
            }`}
            aria-hidden
          />
          <span className="text-foreground">{copy.none}</span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={withCord}
          onClick={() => setWithCord(true)}
          className={`flex items-center gap-2.5 rounded-sm border px-3 py-3 text-left text-sm transition-colors ${
            withCord ? "border-foreground bg-background" : "border-border bg-background/60 hover:border-foreground/60"
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 flex-shrink-0 rounded-full border ${
              withCord ? "border-foreground bg-foreground" : "border-border"
            }`}
            aria-hidden
          />
          <span className="text-foreground">{copy.with}</span>
        </button>
      </div>

      {/* Expanded sub-menu — cord length + extras */}
      {withCord && (
        <div className="mt-5 space-y-5 rounded-sm border border-border/70 bg-background/60 p-4 animate-in fade-in slide-in-from-top-1 duration-200">
          {cordVariants.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                {copy.cordLabel}
              </p>
              <div className="mt-2.5 grid grid-cols-3 gap-2" role="radiogroup" aria-label={copy.cordLabel}>
                {cordVariants.map((v) => {
                  const isSelected = cordId === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setCordId(v.id)}
                      className={`flex flex-col items-start gap-0.5 rounded-sm border px-3 py-2.5 text-left text-sm transition-colors ${
                        isSelected
                          ? "border-foreground bg-background"
                          : "border-border bg-background hover:border-foreground/60"
                      }`}
                    >
                      <span className="text-foreground">{v.title}</span>
                      <span className="text-[11px] text-muted-foreground">+{formatMoney(v.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {showExtras && extraVariants.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                {copy.extrasLabel}
              </p>
              <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {extraVariants.map((v) => {
                  const isSelected = extras.has(v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => toggleExtra(v.id)}
                      aria-pressed={isSelected}
                      className={`flex items-center justify-between gap-2.5 rounded-sm border px-3 py-2.5 text-left text-sm transition-colors ${
                        isSelected
                          ? "border-foreground bg-background"
                          : "border-border bg-background hover:border-foreground/60"
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <span
                          className={`inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-[3px] border ${
                            isSelected ? "border-foreground bg-foreground text-background" : "border-border"
                          }`}
                          aria-hidden
                        >
                          {isSelected && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                        </span>
                        <span className="text-foreground">{v.title}</span>
                      </span>
                      <span className="text-[11px] text-muted-foreground">+{formatMoney(v.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <p className="text-[11px] italic text-muted-foreground">{copy.reassure}</p>
        </div>
      )}
    </div>
  );
}
